import {
  Component,
  computed,
  HostBinding,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AbbDateComponent } from '@components/abb-date/abb-date.component';
import { AbbStatusIconComponent } from '@components/abb-status-icon/abb-status-icon.component';
import {
  AbbTableComponent,
  DataTableFilter,
  IColumn,
} from '@components/abb-table/abb-table.component';
import {
  acknowledgeAlarmMutation,
  alarmLogsListQuery,
  alarmsLogsExportQuery,
} from '@graphql/alarm';
import { TranslateService } from '@ngx-translate/core';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { dataProviderFromObservable } from '@utils/data-provider';
import { normalizeDate, normalizeDateSec } from '@utils/datetime';
import { Apollo } from 'apollo-angular';
import { AbbCommonModule } from 'app/abb-common.module';
import {
  AcknowledgeAlarmMutation,
  AcknowledgeAlarmMutationVariables,
  AlarmLogs,
  AlarmLogsListQuery,
  AlarmLogsListQueryVariables,
  AlarmsLogsExportQuery,
  AlarmsLogsExportQueryVariables,
  Status,
} from 'gql/graphql';
import { BehaviorSubject, first, Subscription } from 'rxjs';
import { ImageValueCellComponent } from '@components/abb-table/abb-cells/imageValueCell';
import { OverlayService } from '@services/ui/overlay.service';
import { downloadBase64File } from '@utils/download';
import { ToastService } from '@services/ui/toast.service';
import { LOADING } from '@utils/loader';
import { t } from '@utils/strings';
import { getErrorMessage } from '@utils/errors';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { CommonModule } from '@angular/common';
import { PAGES } from '@constants/constants';
import { PermissionsService } from '@services/user/permissions.service';

type AlarmsDataTableFilter = {
  status: string | null;
  name: string;
  date: {
    start: { date: Date | null; time: boolean | null };
    end: { date: Date | null; time: boolean | null };
  };
};

@Component({
  selector: 'abb-alarms-logs',
  standalone: true,
  imports: [
    AbbCommonModule,
    CommonModule,
    TPipe,
    FormsModule,
    AbbTableComponent,
    AbbStatusIconComponent,
    AbbDateComponent,
    ImageValueCellComponent,
    OverflowTooltipDirective,
  ],
  template: `
    <abb-table
      [columns]="columns()"
      [dataProvider]="dataProvider"
      [defaultFilters]="defaultFilters"
      [filters]="filters()"
      [loading]="loading()"
      [exporting]="exporting()"
      [dataExport]="true"
      [defaultSortBy]="[{ column: 'name', order: 'asc' }]"
      (onExport)="onExport($event)"
    ></abb-table>
    <ng-template #name let-item>
      <div class="flex gap-2 items-center">
        <span
          overflowTooltip
          class="block truncate"
          [ngClass]="{ 'font-semibold': item.status === Status.Alarm }"
          >{{ item.name }}</span
        >
      </div>
    </ng-template>
    <ng-template #status let-item>
      <div class="flex gap-2 items-center">
        <abb-status-icon
          variant="outlined"
          size="small"
          [status]="item.status"
        ></abb-status-icon>
        <span overflowTooltip class="block truncate">
          {{
            (item.status === Status.Ok
              ? 'Okay'
              : item.status === Status.Warning
              ? 'Offline'
              : 'In alarm'
            ) | t
          }}
        </span>
      </div>
    </ng-template>
    <!-- <ng-template #properties let-item>
      <span overflowTooltip class="block truncate">
        @for (p of item.properties; track p.name) {
        {{ p.name }}@if (!$last) {, } }
      </span>
    </ng-template> -->
    <ng-template #devices let-item>
      <span overflowTooltip class="block truncate">
        @for (d of item.devices; track d.name) {
        {{ d.name }}@if (!$last) {, } }
      </span>
    </ng-template>
    <!-- <ng-template #groups let-item>
      <span overflowTooltip class="block truncate">
        @for (g of item.groups; track g.name) {
        {{ g.name }}@if (!$last) {, } }
      </span>
    </ng-template> -->
    <ng-template #triggerTime let-item>
      @if (item.date === null) {
      {{ '-' }}
      } @else {
      <abb-date
        [date]="item.triggerTime"
        [showTime]="true"
        [showTooltip]="true"
      ></abb-date>
      }
    </ng-template>
    <ng-template #acknowledgedBy let-item>
      @if(item.acknowledgedBy) {
      <abb-image-value-cell
        [value]="item.acknowledgedBy.name + ' ' + item.acknowledgedBy.lastName"
        [image]="item.acknowledgedBy.imgUrl"
      />
      } @else { - }
    </ng-template>
    <ng-template #muteAction let-item>
      @if (canEdit() && item.status === Status.Alarm){
      <abb-icon
        (click)="setAsAcknowledged(item)"
        name="mat/notifications_off"
        size="small"
        class="text-abb-primary cursor-pointer"
      ></abb-icon>
      }
    </ng-template>
  `,
  providers: [{ provide: TRANSLATION_PREFIX, useValue: 'alarms.tabs.logs' }],
})
export class AlarmsLogsComponent implements OnDestroy {
  @HostBinding('attr.id') componentid = 'alarms-logs-component';

  readonly prefix = inject(TRANSLATION_PREFIX);
  readonly Status = Status;
  lang = signal('');
  loading = signal(true);
  exporting = signal(false);
  canEdit = computed(() => this.permissionsService.canEditPage(PAGES.ALARMS));

  nameTemplate = viewChild<TemplateRef<unknown>>('name');
  enabledTemplate = viewChild<TemplateRef<unknown>>('enabled');
  propertiesTemplate = viewChild<TemplateRef<unknown>>('properties');
  groupsTemplate = viewChild<TemplateRef<unknown>>('groups');
  devicesTemplate = viewChild<TemplateRef<unknown>>('devices');
  muteActionTemplate = viewChild<TemplateRef<unknown>>('muteAction');
  statusTemplate = viewChild<TemplateRef<unknown>>('status');
  triggerTimeTemplate = viewChild<TemplateRef<unknown>>('triggerTime');
  acknowledgedByTemplate = viewChild<TemplateRef<unknown>>('acknowledgedBy');

  alarmLogs$ = new BehaviorSubject<AlarmLogsListQuery['alarmLogsList']>([]);
  dataProvider = dataProviderFromObservable<
    AlarmLogsListQuery['alarmLogsList'][number],
    AlarmsDataTableFilter
  >(this.alarmLogs$, (el, filter) => {
    return (
      el.name.toLowerCase().includes(filter.name.toLowerCase()) &&
      (!filter.status || el.status === filter.status) &&
      (!filter.date?.start.date ||
        (filter.date.start.time
          ? filter.date.end.date
            ? normalizeDateSec(el.triggerTime) >=
              normalizeDateSec(filter.date?.start.date)
            : normalizeDateSec(el.triggerTime) ===
              normalizeDateSec(filter.date?.start.date)
          : filter.date.end.date
          ? normalizeDate(el.triggerTime) >=
            normalizeDate(filter.date.start.date)
          : normalizeDate(el.triggerTime) ===
            normalizeDate(filter.date.start.date))) &&
      (!filter.date?.end.date ||
        (filter.date.end.time
          ? normalizeDateSec(el.triggerTime) <=
            normalizeDateSec(filter.date.end.date)
          : normalizeDate(el.triggerTime) <=
            normalizeDate(filter.date.end.date)))
    );
  });
  filters = computed<DataTableFilter<AlarmsDataTableFilter>[]>(() => {
    return [
      {
        id: 'name',
        filterType: 'text',
        className: 'w-full',
        label: 'Name',
      },
      {
        id: 'date',
        filterType: 'datetimeRange',
        className: 'w-full',
        label: 'Start Date - End Date',
      },
    ];
  });
  defaultFilters: AlarmsDataTableFilter = {
    status: null,
    name: '',
    date: {
      start: {
        date: null,
        time: null,
      },
      end: {
        date: null,
        time: null,
      },
    },
  };
  columns = computed<IColumn<AlarmLogsListQuery['alarmLogsList'][number]>[]>(
    () => {
      return [
        {
          name: ' ',
          translate: true,
          templateRef: this.muteActionTemplate(),
          tableHeaderCellClass: this.canEdit() ? 'w-0' : 'hidden',
          tableCellClass: this.canEdit() ? 'w-0' : 'hidden',
        },
        {
          id: 'name',
          name: 'Name',
          key: 'name',
          translate: true,
          templateRef: this.nameTemplate(),
        },
        {
          id: 'status',
          name: 'Status',
          templateRef: this.statusTemplate(),
          translate: true,
        },
        {
          id: 'category',
          name: 'Category',
          key: 'category',
          translate: true,
        },
        {
          id: 'conditionString',
          name: 'Condition',
          key: 'conditionString',
          translate: true,
        },
        {
          id: 'triggerTime',
          name: 'Trigger Time',
          translate: true,
          templateRef: this.triggerTimeTemplate(),
        },
        {
          id: 'devices',
          name: 'Devices',
          templateRef: this.devicesTemplate(),
          translate: true,
        },
        {
          id: 'acknowledgedBy',
          name: 'Acknowledged By',
          translate: true,
          templateRef: this.acknowledgedByTemplate(),
        },
      ];
    }
  );

  private readonly subscriptions: Subscription[] = [];
  constructor(
    private readonly translate: TranslateService,
    private readonly overlayService: OverlayService,
    private readonly apollo: Apollo,
    private toast: ToastService,
    private readonly permissionsService: PermissionsService
  ) {
    this.lang.set(translate.currentLang);
    this.subscriptions.push(
      apollo
        .watchQuery<AlarmLogsListQuery, AlarmLogsListQueryVariables>({
          query: alarmLogsListQuery,
        })
        .valueChanges.subscribe({
          next: ({ data, loading }) => {
            this.alarmLogs$.next(data.alarmLogsList);
            this.loading.set(loading);
          },
          error: (err) => {
            this.loading.set(false);
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }

  awaitingRes = inject(LOADING);
  setAsAcknowledged(alarm: AlarmLogs) {
    return new Promise<void>((resolve, reject) => {
      this.openConfirmModal()
        .then(() => {
          this.awaitingRes.set(true);
          this.acknoledgeAlarm(alarm.id)
            .then(resolve)
            .finally(() => this.awaitingRes.set(false));
        })
        .catch((err) => {
          console.log('cancel');
          resolve(err);
        });
    });
  }

  acknoledgeAlarm(id: string) {
    this.loading.set(true);
    return new Promise<void>((resolve, reject) =>
      this.apollo
        .mutate<AcknowledgeAlarmMutation, AcknowledgeAlarmMutationVariables>({
          mutation: acknowledgeAlarmMutation,
          variables: {
            id: id,
          },
          refetchQueries: [{ query: alarmLogsListQuery }],
          awaitRefetchQueries: true,
        })
        .subscribe({
          next: () => {
            this.loading.set(false);
            this.toast.notifySuccess(
              t(this.prefix, `Alarm acknowledged successfully`, this.translate)
            );
            resolve();
          },
          error: () => {
            this.loading.set(false);
            this.toast.notifyError(
              t(
                this.prefix,
                `An error occurred while acknowledging alarm`,
                this.translate
              )
            );
            reject(new Error('An error occurred while acknowledging alarm'));
          },
        })
    );
  }

  onExport(filters: any) {
    if (!filters.date.start.date || !filters.date.end.date) {
      this.toast.notifyWarning(
        t(this.prefix, `Please enter a date range`, this.translate)
      );
      return;
    }
    this.exporting.set(true);
    const fromDate = filters.date.start.date;
    const toDate = filters.date.end.date;
    this.subscriptions.push(
      this.apollo
        .query<AlarmsLogsExportQuery, AlarmsLogsExportQueryVariables>({
          query: alarmsLogsExportQuery,
          variables: {
            fromTs: fromDate ?? null,
            toTs: toDate ?? null,
          },
        })
        .pipe(first())
        .subscribe({
          next: ({ data }) => {
            downloadBase64File(data.alarmsLogsExport);
            this.exporting.set(false);
            this.toast.notifySuccess(
              t(this.prefix, `File exported successfully`, this.translate)
            );
          },
          error: () => {
            this.exporting.set(false);
            this.toast.notifyError(
              t(
                this.prefix,
                `An error occurred while exporting file`,
                this.translate
              )
            );
          },
        })
    );
  }

  private openConfirmModal() {
    return this.overlayService.openAlert({
      message: `${t(
        this.prefix,
        'Do you confirm to set as acknowledged this alarm?',
        this.translate
      )}`,
      htmlMessage: true,
      confirmText: `${t(this.prefix, 'Set as acknowledged', this.translate)}`,
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
